/**
 * Bank Account Update Resolution
 * Phase 2 document for cash/bank account contributions
 * Secretary Treasurer signature required
 */

import { replaceMergeFields, getAllMergeFields } from './mergeFields';

export function generateBankAccountUpdateResolutionHtml(
  workflowData: any,
  unaData: any
): string {
  const mergeFields = getAllMergeFields(workflowData, unaData);
  
  const template = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page {
      size: Letter;
      margin: 0.75in;
      @bottom-center {
        content: "Page " counter(page) " of " counter(pages);
        font-family: Cambria, Georgia, 'Times New Roman', serif;
        font-size: 9pt;
        color: #666;
      }
    }
    body {
      font-family: Cambria, Georgia, 'Times New Roman', serif;
      font-size: 11pt;
      line-height: 1.85;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 0;
      color: #1a1a1a;
    }
    .header {
      text-align: center;
      border-bottom: 2px solid #c9a961;
      padding-bottom: 18pt;
      margin-bottom: 20pt;
    }
    h1 {
      font-size: 18pt;
      margin: 0 0 8pt 0;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.12em;
    }
    .subtitle {
      font-size: 10pt;
      font-style: italic;
      color: #666;
      margin-top: 8pt;
    }
    .resolution-intro {
      text-align: center;
      margin: 20pt 0;
      font-weight: bold;
      font-size: 10pt;
    }
    .info-box {
      border: 1px solid #ccc;
      padding: 16pt;
      margin: 16pt 0;
      background-color: #fafafa;
      border-radius: 4pt;
    }
    .section-title {
      font-weight: bold;
      font-size: 12pt;
      text-align: center;
      margin: 20pt 0 14pt 0;
      border-bottom: 1px solid #bfbfbf;
      padding-bottom: 6pt;
      color: #262626;
    }
    .clause {
      margin: 14pt 0;
      text-align: justify;
    }
    .clause-label {
      font-weight: bold;
      text-transform: uppercase;
    }
    .signature-section {
      margin-top: 36pt;
      page-break-inside: avoid;
    }
    .signature-block {
      margin: 24pt 0;
      padding: 20pt;
      border: 1px solid #ddd;
      border-radius: 4pt;
      background-color: #fafafa;
    }
    .signature-line {
      border-bottom: 2px solid #333;
      width: 3.5in;
      min-height: 0.6in;
      margin: 20pt 0 8pt 0;
      background-color: white;
    }
    .signature-label {
      font-size: 10pt;
      font-weight: 600;
    }
    .footer {
      margin-top: 30pt;
      padding-top: 16pt;
      border-top: 1px solid #ccc;
      text-align: center;
      font-size: 9pt;
      color: #666;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>Resolution Authorizing Bank Account Updates</h1>
    <div class="subtitle">
      For Private Ecclesiastical Financial Institution Notification
    </div>
    <div style="margin-top: 10pt; font-size: 10pt;">
      {{una_name}}
    </div>
  </div>
  
  <div class="resolution-intro">
    A Resolution of {{una_name}}, an unincorporated nonprofit association operating 
    within private ecclesiastical jurisdiction in the State of {{una_state}}, 
    authorizing the {{primary_officer_role}} to update financial institution records 
    following a private member contribution.
  </div>
  
  <div class="info-box">
    <strong>Account Information:</strong><br>
    Bank Name: {{bank_name}}<br>
    Account Type: {{account_type}}<br>
    Contributor: {{contributor_name}}<br>
    Contribution Date: {{contribution_date}}
  </div>
  
  <div class="section-title">RECITALS</div>
  
  <div class="clause">
    <span class="clause-label">WHEREAS,</span> {{contributor_name}} ("Contributor"), 
    a member of {{una_name}} ("Association"), has made a voluntary private member 
    contribution of certain bank account funds to the Association as documented in 
    the Authorization Resolution executed on {{contribution_date}}; and
  </div>
  
  <div class="clause">
    <span class="clause-label">WHEREAS,</span> the Association has formally accepted 
    this contribution through its Resolution of Acceptance, thereby establishing the 
    contributed funds as ecclesiastical property held in trust for the benefit of 
    the membership; and
  </div>
  
  <div class="clause">
    <span class="clause-label">WHEREAS,</span> it is necessary to notify the financial 
    institution of the ecclesiastical stewardship status of these funds and to authorize 
    appropriate account signatories and access; and
  </div>
  
  <div class="clause">
    <span class="clause-label">WHEREAS,</span> the {{primary_officer_role}} of the Association 
    has been designated as the responsible steward for all financial assets and accounts 
    under the Association's governing documents; and
  </div>
  
  <div class="clause">
    <span class="clause-label">WHEREAS,</span> the Association desires to maintain proper 
    financial controls and accountability over all contributed ecclesiastical funds.
  </div>
  
  <div class="section-title">RESOLUTIONS</div>
  
  <div class="clause">
    <span class="clause-label">NOW, THEREFORE, BE IT RESOLVED,</span> that the {{primary_officer_role}} 
    of {{una_name}} is hereby authorized and directed to contact {{bank_name}} 
    and provide notice of the ecclesiastical stewardship status of the contributed funds;
  </div>
  
  <div class="clause">
    <span class="clause-label">RESOLVED FURTHER,</span> that the {{primary_officer_role}} is 
    authorized to request that the financial institution update its records to reflect 
    that these funds are held as private ecclesiastical property under the stewardship 
    of {{receiving_entity}} for the benefit of {{una_name}};
  </div>
  
  <div class="clause">
    <span class="clause-label">RESOLVED FURTHER,</span> that the {{primary_officer_role}} is 
    authorized to designate appropriate account signatories and to establish banking 
    access protocols consistent with the Association's governing documents and financial 
    policies;
  </div>
  
  <div class="clause">
    <span class="clause-label">RESOLVED FURTHER,</span> that the {{primary_officer_role}} shall 
    provide the financial institution with certified copies of this Resolution, the 
    Authorization Resolution, and the Acceptance Resolution as evidence of the proper 
    ecclesiastical authorization for this contribution and account update;
  </div>
  
  <div class="clause">
    <span class="clause-label">RESOLVED FURTHER,</span> that any and all actions taken 
    by the {{primary_officer_role}} in furtherance of these resolutions are hereby ratified 
    and approved, provided such actions are consistent with the Association's governing 
    documents and the private ecclesiastical nature of this contribution;
  </div>
  
  <div class="clause">
    <span class="clause-label">RESOLVED FURTHER,</span> that this Resolution shall be 
    maintained in the permanent ecclesiastical records of {{una_name}} as evidence of 
    proper stewardship authorization.
  </div>
  
  <div class="signature-section">
    <!-- BOARD_MEMBER_SIGNATURES -->
    
    <div class="signature-block" style="margin-top: 30pt;">
      <h3 style="font-size: 13pt; margin-bottom: 14pt; border-bottom: 1px solid #bfbfbf; padding-bottom: 6pt; color: #262626;">{{primary_officer_role}} Attestation</h3>
      <div>EXECUTED this {{current_date}}</div>
      <div style="margin-top: 10pt;">By the {{primary_officer_role}} of {{una_name}}:</div>
      <div class="signature-line" data-signer-id="{{treasury_secretary_id}}" data-signature-role="primary_officer"></div>
      <div class="signature-label">
        {{treasury_secretary_name}}, {{primary_officer_role}}<br>
        {{una_name}}
      </div>
    </div>
  </div>
  
  <div class="footer">
    Resolution prepared {{current_date}}<br>
    For private ecclesiastical bank account update authorization<br>
    {{una_name}}, {{una_state}}
  </div>
</body>
</html>
  `;
  
  return replaceMergeFields(template, mergeFields);
}
